import { Button, SelectBox } from 'devextreme-react';
import { confirm } from 'devextreme/ui/dialog';
import React, { useCallback, useState } from 'react';
import useSWR from 'swr';
import { deleteApi, postApi } from '../../../../../apiUtils';
import { useAssetClass } from '../../../../../contexts/assetClass';
import { useTranslation } from '../../../../../contexts/translation';
import { Asset } from '../../../../../types/asset.types';
import { TaskRelatedAsset } from '../../../../../types/taskRelatedAsset.types';
import { API_URL, fetcher } from '../../../../../utils/apiUrl';
import { swrConfig } from '../../../../Assets/AssetsEdit';
import './relatedAsset.styles.scss';

interface Props {
  taskId: number;
}

export const RelatedAsset = ({ taskId }: Props) => {
  const { assetClass } = useAssetClass();
  const { translate } = useTranslation();
  const transFormName = 'Task.UpdateServiceDesk';
  const [selectedAssetId, setSelectedAssetId] = useState<number | null>(null);

  const { data: assets } = useSWR<Asset[]>(
    `${API_URL.ASSET_LIST_BY_CLASS}?classId=${assetClass}`,
    fetcher,
    swrConfig,
  );

  const { data: relatedAssets, mutate: refetchRelatedAssets } = useSWR<TaskRelatedAsset[]>(
    `${API_URL.TASK_RELATED_ASSET_LIST}?parentId=${taskId}`,
    fetcher,
    swrConfig,
  );

  const submitPost = useCallback(async () => {
    if (!selectedAssetId) return;

    await postApi({
      url: API_URL.TASK_RELATED_ASSET_CREATE,
      data: {
        taskId,
        assetId: selectedAssetId,
      },
      callAfterSuccess: async (data) => {
        refetchRelatedAssets();
        setSelectedAssetId(null);
      },
    });
  }, [selectedAssetId]);

  const handleDelete = useCallback(async (id: number) => {
    const confirmResult = await confirm(
      translate!('Do you really want to delete related asset?', transFormName),
      translate!('Confirm', ''),
    );
    if (!confirmResult) return;

    await deleteApi({
      url: `${API_URL.TASK_RELATED_ASSET_DELETE}?id=${id}`,
      id: id,
      callAfterSuccess: () => {
        refetchRelatedAssets();
      },
    });
  }, []);

  return (
    <div className="related-assets">
      <div className="title">{translate!('Related assets', transFormName)}</div>
      <div className="form">
        <SelectBox
          dataSource={assets}
          valueExpr="id"
          displayExpr="name"
          value={selectedAssetId}
          onValueChanged={(e) => setSelectedAssetId(e.value)}
          placeholder={translate!('Select related asset', transFormName)}
          searchEnabled={true}
          buttons={[
            {
              name: 'customButton',
              location: 'after',
              options: {
                icon: 'add',
                stylingMode: 'text',
                type: 'default',
                onClick: () => {
                  submitPost();
                },
              },
            },
          ]}
        />
      </div>
      <div className="list">
        {relatedAssets?.map((asset) => {
          return (
            <div key={asset.id} className="asset">
              <div className="name">{asset.assetName}</div>
              <Button
                icon="remove"
                className="remove"
                stylingMode="text"
                type="default"
                onClick={() => handleDelete(asset.id)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
